/**
 * Text measurement: platform.measureText when a platform is attached, otherwise the deterministic
 * headless width table (also used by NodePlatform).
 */
import { rt } from '../player/runtime';
import type { FontSpec } from '../platform/types';

const NARROW = new Set(Array.from("iljtfI!|.,:;'`()[]{}r\"-"));
const WIDE = new Set(Array.from('mwMW@%шщжмыюШЩЖМЮЫфФ'));

/** Headless glyph advance as a fraction of the font size (loosely Tahoma/Arial-like). */
export function headlessCharWidth(code: number): number {
  const ch = String.fromCharCode(code);
  if (code === 32 || code === 0xa0) return 0.3;
  if (code === 9) return 1.2;
  if (code < 32) return 0;
  if (NARROW.has(ch)) return 0.3;
  if (WIDE.has(ch)) return 0.82;
  if (code >= 48 && code <= 57) return 0.55;
  if (code >= 65 && code <= 90) return 0.64;
  if (code >= 97 && code <= 122) return 0.52;
  if (code >= 0x410 && code <= 0x42f) return 0.66;
  if (code >= 0x430 && code <= 0x44f) return 0.54;
  if (code === 0x401) return 0.64;
  if (code === 0x451) return 0.54;
  return 0.58;
}

export function headlessMeasureText(text: string, font: FontSpec): number {
  let w = 0;
  for (let i = 0; i < text.length; i++) w += headlessCharWidth(text.charCodeAt(i));
  w *= font.size;
  if (font.bold) w *= 1.08;
  return w;
}

/** Advance width of `text`; `noCache`: sensitive text (a password field) that no measurement cache may keep. */
export function measureText(text: string, font: FontSpec, noCache = false): number {
  const p = rt().platform;
  return p ? p.measureText(text, font, noCache) : headlessMeasureText(text, font);
}

export function fontMetrics(font: FontSpec): { ascent: number; descent: number } {
  const p = rt().platform;
  if (p && p.fontMetrics) return p.fontMetrics(font);
  return { ascent: font.size * 1.0005, descent: font.size * 0.207 };
}
